import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingBag, User, Menu, X, Lock } from "lucide-react";

const NAV = [
    { to: "/shop", label: "Shop" },
    { to: "/edits", label: "Edits" },
    { to: "/sell", label: "Sell With Us" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/about", label: "About" },
];

export default function Header() {
    const { user, logout } = useAuth() || {};
    const { count, setOpen } = useCart();
    const { currency, setCurrency, supported } = useCurrency();
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isCheckout = pathname.startsWith("/checkout");

    const closeMenu = () => setMobileOpen(false);

    if (isCheckout) {
        return (
            <header className="rax-header rax-header-minimal" data-testid="site-header-checkout">
                <div className="rax-header-inner" style={{ gridTemplateColumns: "1fr auto 1fr" }}>
                    <Link
                        to="/shop"
                        data-testid="checkout-back-shop"
                        style={{
                            fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em",
                            textTransform: "uppercase", color: "var(--cocoa)", opacity: 0.7,
                            textDecoration: "none", justifySelf: "start",
                        }}
                    >
                        ← Continue shopping
                    </Link>
                    <Link to="/" className="rax-header-lockup" data-testid="site-logo" aria-label="RAX The Edit — home">
                        <span className="m">RAX</span>
                        <span className="s">The Edit</span>
                    </Link>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: 6, justifySelf: "end",
                        fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em",
                        textTransform: "uppercase", color: "var(--cocoa)", opacity: 0.8,
                    }}>
                        <Lock size={12} strokeWidth={1.6} />
                        <span className="mobile-hide">Secure checkout</span>
                        <span className="checkout-lock-mobile">Secure</span>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <>
            <header className="rax-header" data-testid="site-header">
                <div className="rax-header-inner">
                    <button
                        className="mobile-nav-toggle"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open menu"
                        data-testid="mobile-menu-open"
                    >
                        <Menu size={20} strokeWidth={1.4} />
                    </button>

                    <nav className="rax-header-nav" aria-label="Primary">
                        {NAV.map((n) => (
                            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link to="/" className="rax-header-lockup" data-testid="site-logo" aria-label="RAX The Edit — home">
                        <span className="m">RAX</span>
                        <span className="s">The Edit</span>
                    </Link>

                    <div className="rax-header-actions">
                        <select
                            className="rax-currency-picker"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            data-testid="header-currency"
                            aria-label="Reference currency"
                        >
                            {(supported || ["GBP"]).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        {user ? (
                            <Link to={user.role === "admin" || user.role === "staff" ? "/admin" : "/account"} data-testid="header-account" className="mobile-hide">
                                <User size={16} strokeWidth={1.4} />
                                <span>Account</span>
                            </Link>
                        ) : (
                            <Link to="/login" data-testid="header-login" className="mobile-hide">
                                <User size={16} strokeWidth={1.4} />
                                <span>Sign In</span>
                            </Link>
                        )}
                        <button
                            onClick={() => setOpen(true)}
                            aria-label={`Bag with ${count} items`}
                            data-testid="header-bag"
                        >
                            <ShoppingBag size={16} strokeWidth={1.4} />
                            <span>Bag</span>
                            {count > 0 && <span className="bag-count" data-testid="bag-count">{count}</span>}
                        </button>
                    </div>
                </div>
            </header>

            <div className={`mobile-nav ${mobileOpen ? "open" : ""}`} data-testid="mobile-nav">
                <div className="mobile-nav-head">
                    <span className="m">RAX</span>
                    <button
                        className="mobile-nav-close"
                        onClick={closeMenu}
                        aria-label="Close menu"
                        data-testid="mobile-menu-close"
                    >
                        <X size={22} strokeWidth={1.4} />
                    </button>
                </div>
                {NAV.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={closeMenu}
                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        {n.label}
                    </NavLink>
                ))}
                <NavLink to="/contact" onClick={closeMenu} data-testid="mobile-nav-contact">
                    Contact
                </NavLink>
                {user ? (
                    <>
                        <NavLink
                            to={user.role === "admin" || user.role === "staff" ? "/admin" : "/account"}
                            onClick={closeMenu}
                            data-testid="mobile-nav-account"
                        >
                            {user.role === "admin" || user.role === "staff" ? "Admin" : "Account"}
                        </NavLink>
                        <button
                            onClick={async () => {
                                await logout();
                                closeMenu();
                                navigate("/");
                            }}
                            data-testid="mobile-nav-signout"
                            style={{
                                textAlign: "left",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: "14px 0",
                                borderBottom: "1px solid var(--sand-line)",
                                fontFamily: "var(--font-serif)",
                                fontSize: 30,
                                color: "var(--cocoa)",
                            }}
                        >
                            Sign Out
                        </button>
                    </>
                ) : (
                    <NavLink to="/login" onClick={closeMenu} data-testid="mobile-nav-signin">
                        Sign In
                    </NavLink>
                )}
            </div>
        </>
    );
}
